const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

/*
 * ============================================================
 * JIANORA GIFTS - SAFE SCROLL ANIMATIONS
 * ============================================================
 *
 * Goals:
 * - Smooth reveal animations
 * - Mobile-safe scrolling
 * - No scroll position manipulation
 * - No continuous scroll calculations on mobile
 * - No layout jumping caused by repeated class changes
 * - Compatible with Shopify/Dawn
 *
 * ============================================================
 */


/* ============================================================
   SETTINGS
============================================================ */

const MOBILE_BREAKPOINT = 749;


/* ============================================================
   HELPERS
============================================================ */

function isMobileDevice() {
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
}


function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}


/* ============================================================
   SCROLL REVEAL ANIMATION
============================================================ */

function initializeScrollAnimationTrigger(
  rootEl = document,
  isDesignModeEvent = false
) {

  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(
      SCROLL_ANIMATION_TRIGGER_CLASSNAME
    )
  );


  if (animationTriggerElements.length === 0) {
    return;
  }


  /*
   * Shopify Theme Editor
   *
   * Do not run IntersectionObserver animations
   * inside the editor.
   */

  if (isDesignModeEvent) {

    animationTriggerElements.forEach((element) => {

      element.classList.add(
        'scroll-trigger--design-mode'
      );

      element.classList.remove(
        SCROLL_ANIMATION_OFFSCREEN_CLASSNAME
      );

    });

    return;
  }


  /*
   * Reduced motion
   *
   * Show everything immediately.
   */

  if (prefersReducedMotion()) {

    animationTriggerElements.forEach((element) => {

      element.classList.remove(
        SCROLL_ANIMATION_OFFSCREEN_CLASSNAME
      );

      element.classList.remove(
        SCROLL_ANIMATION_CANCEL_CLASSNAME
      );

    });

    return;
  }


  /*
   * Safe IntersectionObserver
   *
   * IMPORTANT:
   *
   * Once an element becomes visible we stop
   * observing it permanently.
   *
   * We do NOT add/remove classes repeatedly
   * while scrolling.
   */

  const observer = new IntersectionObserver(
    (entries, observerInstance) => {

      entries.forEach((entry) => {

        if (!entry.isIntersecting) {
          return;
        }


        const element = entry.target;


        /*
         * Reveal the element once.
         */

        element.classList.remove(
          SCROLL_ANIMATION_OFFSCREEN_CLASSNAME
        );


        element.classList.remove(
          SCROLL_ANIMATION_CANCEL_CLASSNAME
        );


        /*
         * Preserve cascade animation support.
         */

        if (element.hasAttribute('data-cascade')) {

          const order =
            Array.from(
              element.parentElement?.children || []
            ).indexOf(element);

          if (order >= 0) {

            element.style.setProperty(
              '--animation-order',
              order
            );

          }

        }


        /*
         * VERY IMPORTANT:
         *
         * Stop watching this element.
         */

        observerInstance.unobserve(element);

      });

    },
    {
      /*
       * Start the animation slightly before
       * the element reaches the viewport.
       */

      rootMargin: '0px 0px -10% 0px',

      threshold: 0.01
    }
  );


  /*
   * Start observing.
   */

  animationTriggerElements.forEach((element) => {

    /*
     * Don't animate elements that are already
     * inside the viewport when the page loads.
     *
     * This prevents the initial page from
     * appearing to jump.
     */

    const rect = element.getBoundingClientRect();

    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight;


    if (
      rect.top < viewportHeight &&
      rect.bottom > 0
    ) {

      element.classList.remove(
        SCROLL_ANIMATION_OFFSCREEN_CLASSNAME
      );

      return;

    }


    observer.observe(element);

  });

}


/* ============================================================
   SAFE ZOOM ANIMATION
============================================================ */

/*
 * IMPORTANT:
 *
 * The original Dawn code continuously listens to
 * the window scroll event and calculates the percentage
 * of every image that has been viewed.
 *
 * That is exactly the type of behavior we want to avoid
 * on mobile.
 *
 * Therefore:
 *
 * MOBILE:
 *   Zoom animation is completely disabled.
 *
 * DESKTOP:
 *   Use a simple IntersectionObserver.
 *
 * No continuous scroll listener.
 */

function initializeScrollZoomAnimationTrigger() {

  /*
   * Never run zoom animations when reduced motion
   * is enabled.
   */

  if (prefersReducedMotion()) {
    return;
  }


  /*
   * Disable zoom completely on mobile.
   *
   * This is intentional.
   *
   * It prevents the zoom animation from competing
   * with mobile touch scrolling.
   */

  if (isMobileDevice()) {

    const mobileZoomElements = Array.from(
      document.getElementsByClassName(
        SCROLL_ZOOM_IN_TRIGGER_CLASSNAME
      )
    );


    mobileZoomElements.forEach((element) => {

      /*
       * Force normal scale.
       */

      element.style.setProperty(
        '--zoom-in-ratio',
        '1'
      );

    });


    return;
  }


  /*
   * Desktop zoom elements.
   */

  const animationTriggerElements = Array.from(
    document.getElementsByClassName(
      SCROLL_ZOOM_IN_TRIGGER_CLASSNAME
    )
  );


  if (animationTriggerElements.length === 0) {
    return;
  }


  /*
   * Desktop-only IntersectionObserver.
   *
   * No window scroll listener.
   */

  const observer = new IntersectionObserver(
    (entries, observerInstance) => {

      entries.forEach((entry) => {

        const element = entry.target;


        if (entry.isIntersecting) {

          /*
           * Start from normal scale.
           */

          element.style.setProperty(
            '--zoom-in-ratio',
            '1'
          );


          /*
           * We only need to initialize this once.
           */

          observerInstance.unobserve(element);

        }

      });

    },
    {
      rootMargin: '0px 0px -10% 0px',

      threshold: 0.01
    }
  );


  animationTriggerElements.forEach((element) => {

    /*
     * Keep the normal scale until
     * the element enters the viewport.
     */

    element.style.setProperty(
      '--zoom-in-ratio',
      '1'
    );


    observer.observe(element);

  });

}


/* ============================================================
   INITIALIZATION
============================================================ */

function initializeJianoraSafeAnimations() {

  initializeScrollAnimationTrigger();

  initializeScrollZoomAnimationTrigger();

}


/* ============================================================
   PAGE LOAD
============================================================ */

if (document.readyState === 'loading') {

  document.addEventListener(
    'DOMContentLoaded',
    initializeJianoraSafeAnimations,
    {
      once: true
    }
  );

} else {

  initializeJianoraSafeAnimations();

}


/* ============================================================
   SHOPIFY THEME EDITOR SUPPORT
============================================================ */

if (
  typeof Shopify !== 'undefined' &&
  Shopify.designMode
) {

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initializeScrollAnimationTrigger(
        event.target,
        true
      );

    }
  );


  document.addEventListener(
    'shopify:section:reorder',
    () => {

      initializeScrollAnimationTrigger(
        document,
        true
      );

    }
  );

}